import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { logger } from "../../../../utils/Logger";

interface SimulationDiagramLoadingIndicatorProps {
  isVisible: boolean;
  startTime?: number;
}

const SimulationDiagramLoadingIndicator: React.FC<
  SimulationDiagramLoadingIndicatorProps
> = ({ isVisible, startTime }) => {
  const theme = useTheme();
  const [now, setNow] = useState<number>(Date.now());

  // Update timer for progress calculation
  useEffect(() => {
    let timer: number;
    if (isVisible && startTime) {
      timer = window.setInterval(() => setNow(Date.now()), 1000);
    }
    return () => {
      clearInterval(timer);
    };
  }, [isVisible, startTime]);

  // Don't show if not visible
  if (!isVisible) {
    return null;
  }

  // Calculate progress (36 seconds max, cap at 90% until completion) - doubled from 18 seconds
  const calculateProgress = (): number => {
    if (!startTime) return 0;

    const elapsed = now - startTime;
    return Math.min((elapsed / 36000) * 90, 90); // Cap at 90% until completion
  };

  const progress = calculateProgress();

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bgcolor="rgba(255, 255, 255, 0.95)"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      zIndex={1000}
      borderRadius="0 0 15px 15px"
      sx={{
        backdropFilter: "blur(2px)",
      }}
    >
      <Box width="80%" maxWidth={400} textAlign="center">
        <Typography
          variant="h6"
          mb={3}
          sx={{
            fontWeight: 500,
            color: theme.custom.darkerGrey,
          }}
        >
          Generating DataFlow Diagram
        </Typography>

        <Typography
          variant="body2"
          mb={2}
          sx={{
            opacity: 0.8,
            fontWeight: "normal",
            color: theme.custom.darkerGrey,
          }}
        >
          Analyzing code and creating execution steps ({Math.round(progress)}%)
        </Typography>

        <Box
          position="relative"
          height={8}
          borderRadius={1}
          overflow="hidden"
          bgcolor={theme.custom.lightGrey}
          mb={2}
        >
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width={`${progress}%`}
            bgcolor={theme.custom.pink}
            sx={{
              transition: "width 0.5s ease, box-shadow 0.5s ease",
              ...(progress >= 90
                ? { boxShadow: `0 0 8px ${theme.custom.pink}` }
                : {}),
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            opacity: 0.6,
            fontStyle: "italic",
            color: theme.custom.darkerGrey,
          }}
        >
          This takes 45 seconds on average to complete due to Gemini API
          limitations.
        </Typography>
      </Box>
    </Box>
  );
};

export default SimulationDiagramLoadingIndicator;
